// Human-friendly entity references — PLATFORM STANDARD (owner direction
// 2026-07-21).
//
// Operators should scan/discuss "SP-0834E", not
// "proc_job_5cb6cad6-e7c7-44f4-9099-fc9512c0834e". UUIDs stay canonical for
// audit, API, storage, and navigation; the short reference is display-only,
// derived from the canonical id's immutable tail, so it is stable for the
// lifetime of the entity.
//
// One registry entry per entity type. Song Processing (SP) is live today;
// the remaining prefixes are reserved by the owner and light up as each
// surface adopts the standard — add the entry here, then reuse
// formatEntityRef / buildEntityRefs / normalizeRefSearchTerm unchanged.
//
// Renames (owner 2026-07-21: "Processing Jobs" → "Song Processing", PJ → SP)
// only change the display prefix — canonical ids are untouched, so existing
// jobs show the new prefix immediately. Retired prefixes stay searchable via
// legacyPrefixes so old refs in notes/chat keep resolving.
//
// Collision rule (owner: "do not truncate in a way that can silently collide
// without disambiguation"): within one rendered set, any refs sharing the
// same suffix are extended (5 → 7 → 9 … hex chars) until unique.

interface EntityRefKindEntry {
  prefix: string;
  label: string;
  /** Retired display prefixes still accepted by search normalization. */
  legacyPrefixes?: readonly string[];
}

export const ENTITY_REF_KINDS = {
  processingJob: {
    prefix: "SP",
    label: "Song Processing",
    legacyPrefixes: ["PJ"],
  },
  track: { prefix: "TR", label: "Track" },
  album: { prefix: "AL", label: "Album" },
  artist: { prefix: "AR", label: "Artist" },
  recordLabel: { prefix: "RL", label: "Record Label" },
  channel: { prefix: "CH", label: "Channel" },
  genre: { prefix: "GN", label: "Genre" },
  subgenre: { prefix: "SG", label: "Subgenre" },
} as const satisfies Record<string, EntityRefKindEntry>;

export type EntityRefKind = keyof typeof ENTITY_REF_KINDS;

const BASE_SUFFIX_LENGTH = 5;
const MAX_SUFFIX_LENGTH = 32;

/** Hex/alphanumeric tail of the canonical id (uuid dashes ignored). */
function idTail(id: string, length: number): string {
  const compact = id.replace(/[^0-9a-zA-Z]/g, "");
  return compact.slice(-length).toUpperCase();
}

/** Canonical short reference, e.g. "SP-0834E" / "TR-93B1F". */
export function formatEntityRef(
  kind: EntityRefKind,
  id: string,
  suffixLength = BASE_SUFFIX_LENGTH,
): string {
  if (!id) return "—";
  return `${ENTITY_REF_KINDS[kind].prefix}-${idTail(id, suffixLength)}`;
}

/**
 * Collision-safe refs for a rendered set of canonical ids (all of one kind).
 * Non-colliding ids get the standard 5-char ref; colliding groups are
 * extended until unique (distinct ids always diverge before the full tail).
 */
export function buildEntityRefs(
  kind: EntityRefKind,
  ids: readonly string[],
): Map<string, string> {
  const refs = new Map<string, string>();
  const unique = Array.from(new Set(ids.filter(Boolean)));
  let unresolved = unique;
  let length = BASE_SUFFIX_LENGTH;

  while (unresolved.length > 0 && length <= MAX_SUFFIX_LENGTH) {
    const bySuffix = new Map<string, string[]>();
    for (const id of unresolved) {
      const ref = formatEntityRef(kind, id, length);
      const group = bySuffix.get(ref);
      if (group) group.push(id);
      else bySuffix.set(ref, [id]);
    }
    const stillColliding: string[] = [];
    for (const [ref, group] of bySuffix) {
      if (group.length === 1) refs.set(group[0], ref);
      else stillColliding.push(...group);
    }
    unresolved = stillColliding;
    length += 2;
  }
  // Distinct ids always diverge before 32 chars; guard for safety anyway.
  for (const id of unresolved) refs.set(id, formatEntityRef(kind, id, MAX_SUFFIX_LENGTH));
  return refs;
}

/**
 * Search normalization: operators may paste either the short reference
 * ("SP-0834E", "sp0834e", or a retired legacy prefix) or any fragment of
 * the canonical id. Short refs
 * are lowered and stripped of the kind prefix so the server's
 * case-insensitive contains() match finds the canonical id tail. Everything
 * else passes through unchanged.
 */
export function normalizeRefSearchTerm(kind: EntityRefKind, term: string): string {
  const trimmed = term.trim();
  const entry: EntityRefKindEntry = ENTITY_REF_KINDS[kind];
  const prefixes = [entry.prefix, ...(entry.legacyPrefixes ?? [])];
  for (const prefix of prefixes) {
    const refMatch = new RegExp(`^${prefix}[-_ ]?([0-9a-z]{3,32})$`, "i").exec(trimmed);
    if (refMatch) return refMatch[1].toLowerCase();
  }
  return trimmed;
}

/**
 * Multi-kind search normalization for surfaces whose backend matches more
 * than one entity id (e.g. Song Processing search matches Job ID AND Track
 * ID). The first kind whose prefix matches wins; free text passes through.
 */
export function normalizeAnyRefSearchTerm(
  kinds: readonly EntityRefKind[],
  term: string,
): string {
  const trimmed = term.trim();
  for (const kind of kinds) {
    const normalized = normalizeRefSearchTerm(kind, trimmed);
    if (normalized !== trimmed) return normalized;
  }
  return trimmed;
}
